import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry, faBolt, faBoxOpen, faTag, faLayerGroup, faStore, faPlug, faBatteryFull, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { ScaleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { formatPrice, formatKwota } from '../utils';
import { KRAJ } from '../kraj';
import i18n from '../i18n';
import AvailabilityBadge from './AvailabilityBadge';
import { useSelection, selectionKey } from '../context/SelectionContext';
import { ProductImage } from './ProductImage';
import PriceHistoryChart from './PriceHistoryChart';
import { buildModelUrl } from '../utils/modelUrl';
import TypeChips from './TypeChips';
import { etykietaMocySkrocona } from '../utils/etykietaMocy';

// plain=false wyłącza truncate (dla chipów typu, które mają się zawijać, nie ucinać).
const Attr = ({ icon, label, children, plain = true }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1.5 text-gray-400">
      <FontAwesomeIcon icon={icon} className="text-sm" />
      <span className="text-xs uppercase tracking-wide">{label}</span>
    </div>
    <div className={`mt-1 text-base font-semibold text-gray-800 ${plain ? 'truncate' : ''}`}>{children}</div>
  </div>
);

const emptyDash = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);
// Wartości z bazy (rodzaj, fazy, napięcie) pokazujemy przez te same etykiety co
// w filtrach (lista:filtry.schemat.wartosci.<pole>.<wartość>); nieznana wartość
// zostaje bez zmian.
const etykieta = (pole, v) => (v ? i18n.t(`lista:filtry.schemat.wartosci.${pole}.${v}`, { defaultValue: v }) : v);

const ModelProduct = ({ model, productType = 'panel' }) => {
  const { t } = useTranslation('lista');
  const { isCompared, toggleComparison, openHistoryChart, closeHistoryChart, isHistoryChartOpen } = useSelection();
  const historyKey = selectionKey(productType, model.id);
  const historyOpen = isHistoryChartOpen(historyKey);
  const priceMin = formatKwota(model.price_min, 0);
  const wpMin = formatPrice(model.price_wp_min, 2);
  const cid = model.cheapest_offer_id;
  const cmp = cid != null && isCompared(productType, cid);

  // Główny parametr rozmiaru + trzeci parametr zależnie od typu.
  const sizeLabel = productType === 'magazyn' ? t('model.pojemnosc') : t('model.moc');
  const sizeIcon = productType === 'magazyn' ? faBatteryFull : faBolt;
  const sizeValue =
    productType === 'panel' ? (model.power != null ? `${model.power} Wp` : '—')
    : productType === 'inwerter' ? (model.power != null ? `${model.power} kW` : '—')
    // Ta sama funkcja co na kafelku oferty — moc wallboxa zawsze z kwalifikatorem prądu.
    : productType === 'wallbox' ? etykietaMocySkrocona(model)
    : (model.capacity != null ? `${model.capacity} kWh` : '—');

  // Wallboxy nie mają pól `type` ani `voltage` z magazynów — bez własnej gałęzi
  // widok grupowany pokazywał w obu miejscach myślnik.
  const rodzajWallboxa = ['ac_wallbox', 'ac_stacja', 'dc_stacja', 'mobilna_evse'].includes(model.product_class)
    ? t(`model.rodzaj_wallboxa.${model.product_class}`) : undefined;

  const zlaczeWallboxa = ['gniazdo_Type2', 'kabel_Type2', 'kabel_Type1'].includes(model.connector_type)
    ? t(`model.zlacze_wallboxa.${model.connector_type}`) : undefined;

  const thirdAttr =
    productType === 'panel'
      ? <Attr icon={faTag} label={t('model.cena_za_wp')}>{wpMin !== null ? t('model.od_cena_wp', { cena: wpMin, waluta: KRAJ.symbol }) : '—'}</Attr>
      : productType === 'inwerter'
      ? <Attr icon={faPlug} label={t('model.fazy')}>{emptyDash(etykieta('phase', model.phase))}</Attr>
      : productType === 'wallbox'
      ? <Attr icon={faPlug} label={t('model.zlacze')}>{emptyDash(zlaczeWallboxa)}</Attr>
      : <Attr icon={faBolt} label={t('model.napiecie')}>{emptyDash(etykieta('voltage', model.voltage))}</Attr>;

  const compareOffer = () => {
    if (cid == null) return;
    toggleComparison(productType, {
      id: cid, name: model.name, img_url: model.img_url, price: model.price_min, price_netto: null,
      price_wp: model.price_wp_min, manufacturer: model.manufacturer,
      power: model.power, capacity: model.capacity, type: model.type, types: model.types,
      phase: model.phase, voltage: model.voltage,
      availability: model.availability_any ? 'Dostępny' : null, seller: null, link: null,
    });
  };

  return (
    <li className="relative flex flex-col md:flex-row gap-4 p-4 mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
      <Link to={buildModelUrl(productType, model)} className="flex flex-col md:flex-row gap-4 flex-grow min-w-0" style={{ textDecoration: 'none', color: 'inherit' }}>
        <ProductImage src={model.img_url} productType={productType} alt={model.name} className="w-full md:w-48 h-48 object-contain rounded-xl border border-gray-100 bg-white p-2 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-lg sm:text-xl text-gray-900 line-clamp-2">{model.name}</h3>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
            <Attr icon={faIndustry} label={t('model.producent')}>{emptyDash(model.manufacturer)}</Attr>
            <Attr icon={sizeIcon} label={sizeLabel}>{sizeValue}</Attr>
            <Attr icon={faBoxOpen} label={t('model.dostepnosc')}>
              {model.availability_any ? <AvailabilityBadge value="Dostępny" /> : <span className="text-gray-400">{t('model.sprawdz_oferty')}</span>}
            </Attr>
            <Attr icon={faLayerGroup} label={t('model.rodzaj')} plain={productType !== 'panel'}>
              {productType === 'panel'
                ? <TypeChips types={model.types} type={model.type} />
                : productType === 'wallbox'
                ? emptyDash(rodzajWallboxa)
                : emptyDash(etykieta('types', model.type))}
            </Attr>
            <Attr icon={faStore} label={t('model.oferty')}><span className="text-blue-600">{t('model.liczba_ofert', { count: model.offer_count ?? 0 })}</span></Attr>
            {thirdAttr}
          </div>
        </div>
      </Link>
      <div className="flex flex-row md:flex-col items-center justify-between md:justify-center gap-3 md:pl-5 md:border-l md:border-gray-100 md:min-w-[170px]" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); compareOffer(); }} disabled={cid == null} aria-label={t('model.dodaj_do_porownania')} aria-pressed={cmp} title={t('model.dodaj_do_porownania')} className={`flex items-center justify-center rounded-lg border w-8 h-8 transition-colors ${cmp ? 'border-blue-300 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-400 hover:bg-gray-50'} ${cid == null ? 'opacity-40 cursor-not-allowed' : ''}`}>
          <ScaleIcon className="w-4 h-4" />
        </button>
        <div className="text-left md:text-center">
          <p className="text-xs text-gray-400 uppercase tracking-wide">{t('model.cena_od')}</p>
          <div className="flex items-center gap-2 md:justify-center">
            <p className="font-extrabold text-blue-600 text-2xl md:text-3xl whitespace-nowrap">{priceMin !== null ? priceMin : t('model.brak_ceny')}</p>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); openHistoryChart(historyKey); }}
              aria-label={t('model.historia_ceny')}
              title={t('model.historia_ceny')}
              className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-blue-200 text-blue-500 hover:bg-blue-50 transition-colors shrink-0 text-xs"
            >
              <FontAwesomeIcon icon={faChartLine} />
            </button>
          </div>
        </div>
        <Link to={buildModelUrl(productType, model)} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors whitespace-nowrap">{t('model.zobacz_oferty', { liczba: model.offer_count ?? 0 })}</Link>
      </div>
      <PriceHistoryChart modelId={model.id} category={productType} modelName={model.name} open={historyOpen} onClose={() => closeHistoryChart(historyKey)} />
    </li>
  );
};

export default ModelProduct;
